/**
 * Domain categorization of content types : the visual groups of the Content browser's
 * "Browse & manage" overview, also reused by the Publication page to pick a per-type icon.
 * Deliberately a LEAF module (no component imports) so consumers stay light to load and test.
 */

/**
 * Lower-cased, diacritic-stripped form of a label, used purely for pattern matching
 * (e.g. "Réserves" → "reserves"). Kept separate from `normalizeContentTypeKey`, which
 * turns accents into "-" and is meant for building stable keys, not fuzzy matching.
 */
export const deaccent = (value?: string | null) =>
	(value ?? "")
		.toLowerCase()
		.normalize("NFD")
		.replace(/[̀-ͯ]/g, "");

export type ContentCategoryId =
	| "editorial"
	| "rates"
	| "securities"
	| "stats"
	| "reports"
	| "institutions"
	| "other";

export interface ContentCategory {
	id: ContentCategoryId;
	label: string;
	hint: string;
}

/**
 * Visual groups for the "Browse & manage" overview, in DISPLAY order : the domains whose
 * data changes most often sit at the top (editorial is published continuously; FX/rates
 * are updated every business day), the slow-moving registries at the bottom. Each card is
 * assigned to exactly one group by {@link categorizeContentType}; empty groups are hidden.
 */
export const CONTENT_CATEGORIES: ContentCategory[] = [
	{
		id: "editorial",
		label: "Actualités & communication",
		hint: "Articles, communiqués, événements, vidéos et pages : publiés en continu."
	},
	{
		id: "rates",
		label: "Taux & marchés",
		hint: "Cours de change, taux directeur et inflation : mis à jour quotidiennement."
	},
	{
		id: "securities",
		label: "Adjudications & titres",
		hint: "Bons BCC, Bons du Trésor, encours et résultats d'adjudication."
	},
	{
		id: "stats",
		label: "Statistiques monétaires & économiques",
		hint: "Monnaie, secteurs, réserves et agrégats publiés périodiquement."
	},
	{
		id: "reports",
		label: "Rapports & publications",
		hint: "Rapports, bulletins, notes de conjoncture et documents PDF."
	},
	{
		id: "institutions",
		label: "Institutions & supervision",
		hint: "Registres des intermédiaires financiers, gouverneurs et supervision."
	},
	{ id: "other", label: "Autres", hint: "Autres types de contenu." }
];

/**
 * MATCH priority (independent of display order): the financial / structural domains are
 * tested before the editorial catch-all so e.g. "Bons BCC : Annonce de l'adjudication"
 * lands in Adjudications rather than Actualités on the bare word "annonce". First match
 * on the de-accented title wins; nothing matched → "other".
 */
const CATEGORY_MATCHERS: Array<{ id: ContentCategoryId; patterns: string[] }> = [
	{ id: "rates", patterns: ["taux", "cours de change", "cours parallele"] },
	{
		id: "securities",
		patterns: [
			"bons",
			"adjudication",
			"encours",
			"refinancement",
			"titres indexes",
			"indice d ajustement",
			"facilites permanentes",
			"prets a court terme"
		]
	},
	{
		id: "stats",
		patterns: [
			"monnaie et credit",
			"secteur exterieur",
			"secteur reel",
			"reserve",
			"coefficient",
			"finances publiques",
			"volume de transaction",
			"bulletin",
			"conden",
			"statistiq"
		]
	},
	{ id: "reports", patterns: ["rapport", "publication", "conjoncture", "fichiers pdf", "dsif"] },
	{
		id: "institutions",
		patterns: [
			"if ",
			"institution",
			"etablissement",
			"billets de banque",
			"gouverneur",
			"surveillance",
			"intermediaires financiers",
			"dsp"
		]
	},
	{ id: "editorial", patterns: ["article", "actualit", "communiqu", "annonce", "event", "video", "page"] }
];

/** Assign a content type to one of the {@link CONTENT_CATEGORIES} for the overview grouping. */
export const categorizeContentType = (title?: string | null, key?: string | null): ContentCategoryId => {
	const hay = `${deaccent(title)} ${deaccent(key)}`;
	for (const matcher of CATEGORY_MATCHERS) {
		if (matcher.patterns.some((pattern) => hay.includes(pattern))) return matcher.id;
	}
	return "other";
};
